import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type SetFilters = (
  patch: Record<string, string>,
  opts?: {
    /** No crear entrada de historial (para escrituras por tipeo). */
    replace?: boolean;
    /** Volver a página 1 — solo si el patch cambió algo. */
    resetPage?: boolean;
  },
) => void;

/**
 * Filtros de listado persistidos en los query params de la URL.
 *
 * La URL es la fuente de verdad: las queries leen de `searchParams`, así los
 * filtros sobreviven al volver desde un detalle, el back/forward funciona y
 * la vista se puede compartir/bookmarkear.
 *
 * `set` borra la key cuando el valor es '' (URL limpia para defaults) y es
 * no-op si el patch no cambia nada — eso evita resets de página espurios
 * cuando el efecto de persistencia corre en el mount.
 */
export function useUrlFilters(): [URLSearchParams, SetFilters] {
  const [searchParams, setSearchParams] = useSearchParams();

  const set = useCallback<SetFilters>(
    (patch, opts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          let changed = false;
          for (const [k, v] of Object.entries(patch)) {
            const cur = next.get(k) ?? '';
            if (v === cur) continue;
            if (v === '') next.delete(k);
            else next.set(k, v);
            changed = true;
          }
          if (!changed) return prev;
          if (opts?.resetPage) next.delete('page');
          return next;
        },
        { replace: opts?.replace },
      );
    },
    [setSearchParams],
  );

  return [searchParams, set];
}
